import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { FlatList, View, } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { format, parseISO, isBefore, startOfToday } from 'date-fns';
import Text from 'design-system/Text';
import StatusBar from 'design-system/StatusBar';
import { AppView } from 'design-system/AppView';
import Button, { HEIGHT } from 'design-system/Button';
import ListEmptyView from 'design-system/ListEmptyView';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import Modal from 'utils/packages/Modal';
import Amplitude from 'utils/amplitude';
import useColors from 'hooks/useColors';
import { muteErrorAndResult, useFetchOld, useFocusEffect } from 'hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useStyles from 'hooks/useStyles';
import { selectCommittedTransactionsForRange, selectCurrentPaydayRangeWithFallback, selectDefaultCurrency, selectExcludedCategoriesCount, } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import Links from 'utils/links';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import ListLoader from 'design-system/ListLoader';
import { Spacer } from 'design-system';
import { getCommitted } from '../actions';
import { selectIncludeIncomeSubscriptions } from '../selectors';
import CommittedSpendingRow from '../components/CommittedSpendingRow';
import { isWeb } from '../../../constants';
const keyExtractor = (item) => {
    if (item.type === 'header' || item.type === 'footer') {
        return item.title;
    }
    if (item.type === 'subscription') {
        return `${item.data.id.toString()}${item.data.prediction?.date}`;
    }
    return item.data.id.toString();
};
const typeExtractor = ({ type }) => type;
const addLastInSectionInfo = (flashListData) => {
    const lastItem = flashListData.pop();
    if (lastItem && 'type' in lastItem && lastItem.type !== 'header') {
        flashListData.push({
            ...lastItem,
            isLastInSection: true,
        });
    }
};
const strings = {
    excludedSpending: 'Excluded spending',
    excludedSpendingMessage: 'Emma excludes your credit card repayments, standing orders and internal transfers, so they don’t double count in your Analytics.',
    excludedCategoriesMessage: 'If you exclude categories, their transactions will also be excluded from your Committed spending.',
    learnMore: 'Learn\xa0more',
    excludedSpendingButton: 'Got it',
    manageExcludedCategories: 'Manage excluded categories',
    stillSeePayments: 'Still see payments you’ve paid or cancelled?',
};
const CommittedSpendingScreen = withAnimatedNavigationHeader(({ navigation, route, onScrollY }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const [, , fetching, fetch] = useFetchOld(muteErrorAndResult);
    const paydayRange = useAppSelector(selectCurrentPaydayRangeWithFallback);
    const toFromObj = useMemo(() => {
        const to = route.params?.toDate;
        const from = route.params?.fromDate;
        if (to && from) {
            return {
                to,
                from,
                toFormatted: format(parseISO(to || paydayRange.to), "do 'of' MMMM"),
            };
        }
        return {
            to: paydayRange.to,
            from: paydayRange.from,
            toFormatted: format(parseISO(to || paydayRange.to), "do 'of' MMMM"),
        };
    }, [
        paydayRange.from,
        paydayRange.to,
        route.params?.fromDate,
        route.params?.toDate,
    ]);
    const openCommittedSpendingHelp = useCallback(() => {
        Links.openKnownLink('committedSpendingHelp');
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.COMMITTED_SPENDING}/>),
        });
    }, [colors.background.light, openCommittedSpendingHelp]);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const currency = useAppSelector(selectDefaultCurrency);
    const { committed, transactions: committedTransactions } = useAppSelector((store) => selectCommittedTransactionsForRange(store, toFromObj.from, toFromObj.to));
    const includeIncome = useAppSelector(selectIncludeIncomeSubscriptions);
    const hasExcludedCategories = useAppSelector(selectExcludedCategoriesCount) > 0;
    const sections = useMemo(() => {
        const array = [];
        const flattenAndPush = (arrayToFlatten, type) => {
            let hasFilledFirstItem = false;
            arrayToFlatten.forEach(({ data }) => {
                data.forEach((subscription) => {
                    array.push({
                        type: type,
                        data: subscription,
                        isFirstInSection: !hasFilledFirstItem,
                    });
                    hasFilledFirstItem = true;
                });
            });
        };
        if (committedTransactions.arrayToBePaid.length) {
            array.push({
                type: 'header',
                title: 'Upcoming',
                total: -committedTransactions.totalToBePaid,
                currency,
            });
            flattenAndPush(committedTransactions.arrayToBePaid, 'subscription');
            addLastInSectionInfo(array);
            const hasPastPredictions = committedTransactions.arrayToBePaid.some(({ data }) => data.some((subscription) => subscription.prediction?.date &&
                isBefore(parseISO(subscription.prediction.date), startOfToday())));
            if (hasPastPredictions) {
                array.push({
                    type: 'footer',
                    title: strings.stillSeePayments,
                });
            }
        }
        if (committedTransactions.arrayPaid.length) {
            array.push({
                type: 'header',
                title: 'Already paid',
                total: -committedTransactions.totalPaid,
                currency,
            });
            flattenAndPush(committedTransactions.arrayPaid, 'transaction');
            addLastInSectionInfo(array);
        }
        return array;
    }, [committedTransactions, currency]);
    const isCurrentPeriod = toFromObj.from === paydayRange.from && toFromObj.to === paydayRange.to;
    useFocusEffect(useCallback(() => {
        fetch(getCommitted(toFromObj.from, toFromObj.to, includeIncome, isCurrentPeriod ? 'currentPeriod' : 'range'));
    }, [toFromObj.from, toFromObj.to, includeIncome, isCurrentPeriod]));
    const bottom = useMarginBottom('bottom', undefined, styles.button);
    const openSubscription = useCallback((data) => {
        if (data?.isInternal) {
            const learnMore = (<Text Text-14 Primary onPress={openCommittedSpendingHelp}>
              {strings.learnMore}
            </Text>);
            if (hasExcludedCategories) {
                Modal.showConfirmation({
                    title: strings.excludedSpending,
                    subtitle: [
                        strings.excludedSpendingMessage,
                        <>
                  {strings.excludedCategoriesMessage} {learnMore}
                </>,
                    ],
                    confirmButtonTitle: strings.manageExcludedCategories,
                    onConfirm: () => navigation.navigateDeprecated('ExcludeCategories', {}),
                    cancelButtonTitle: strings.excludedSpendingButton,
                });
            }
            else {
                Modal.showConfirmation({
                    title: strings.excludedSpending,
                    subtitle: [
                        <>
                  {strings.excludedSpendingMessage} {learnMore}
                </>,
                    ],
                    confirmButtonTitle: strings.excludedSpendingButton,
                });
            }
            return;
        }
        navigation.navigateDeprecated('Subscription', {
            id: data.id,
        });
        Amplitude.logEvent('Committed.OpenSubscriptionMenu');
    }, [openCommittedSpendingHelp, hasExcludedCategories]);
    const onOpenCompare = useCallback((routeWithParams) => {
        navigation.navigateDeprecated(routeWithParams);
        Amplitude.logEvent('Committed.openCompare', {
            type: routeWithParams.name,
        });
    }, []);
    const openSubscriptions = useCallback(() => {
        navigation.navigateDeprecated('Subscriptions');
        Amplitude.logEvent('Committed.OpenSubscriptions');
    }, []);
    const renderItem = useCallback(({ item }) => (<CommittedSpendingRow item={item} onOpenCompare={onOpenCompare} openSubscription={openSubscription} guessedHomeCountry={guessedHomeCountry}/>), [onOpenCompare, openSubscription, guessedHomeCountry]);
    const scrolled = useCallback(({ nativeEvent: { contentOffset: { y }, }, }) => onScrollY(y), [onScrollY]);
    const renderEmpty = useMemo(() => {
        if ((committed && !committed.subscriptions) || fetching) {
            return <ListLoader />;
        }
        return (<ListEmptyView title="No committed payments" style={styles.centered} compact onCard/>);
    }, [committed, fetching, styles.centered]);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    const renderHeader = useMemo(() => sections.length ? (<View style={styles.spendingUntilCard}>
            <Text TextThin-16 OnBrand centered>
              Your spending until{' '}
              <Text Text-16 OnBrand>
                {toFromObj.toFormatted}
              </Text>
            </Text>
          </View>) : (<Spacer.H8 />), [sections.length, styles.spendingUntilCard, toFromObj.toFormatted]);
    const baseProps = {
        onScroll: scrolled,
        contentContainerStyle,
        scrollEventThrottle: 16,
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
        renderItem,
        ListHeaderComponent: renderHeader,
        ListEmptyComponent: renderEmpty,
        data: sections,
        keyExtractor,
    };
    return (<AppView withFrame={false}>
        <View style={styles.content}>
          <StatusBar barStyle="dark-content" animated/>
          {isWeb ? (<FlatList {...baseProps}/>) : (<FlashList {...baseProps} getItemType={typeExtractor}/>)}
          {!isWeb && (<Button brand floating style={bottom} onPress={openSubscriptions} title="Manage your recurring payments"/>)}
        </View>
      </AppView>);
}, ({ colors }) => ({
    title: 'Committed spending',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    content: {
        flex: 1,
    },
    button: {
        position: 'absolute',
        left: rem(16),
        right: rem(16),
    },
    spendingUntilCard: {
        paddingHorizontal: rem(12),
        paddingVertical: rem(16),
        marginHorizontal: rem(16),
        marginBottom: rem(8),
        marginTop: rem(16),
        borderRadius: rem(16),
        backgroundColor: colors.background.brandLight,
    },
    centered: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: rem(16),
    },
}));
export default CommittedSpendingScreen;
